import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { propertyGroups } from '../api/endpoints.js';
import { type PropertyGroupDetail } from '../api/schemas.js';

export function PropertyGroupDetailPage(): React.JSX.Element {
  const id = useParams().propertyGroupId ?? '';
  const query = propertyGroups.useItem(id);
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = propertyGroups.useSoftDelete();

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.PROPERTY_GROUP_READ}
      parent={{ to: '/property-groups', label: 'Property groups' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.PROPERTY_GROUP_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/property-groups/${id}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.PROPERTY_GROUP_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data: PropertyGroupDetail) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete property group"
            description={`${data.name} will be retired. Existing references are retained.`}
            confirmLabel="Delete"
            destructive
            loading={remove.isPending}
            onConfirm={() => {
              void remove.mutateAsync(id).then(() => {
                setConfirmingDelete(false);
                void navigate('/property-groups');
              });
            }}
          />
          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Name">{data.name}</DetailField>
            <DetailField label="Description">{data.description}</DetailField>
            <DetailField label="Status">{data.status}</DetailField>
            <DetailField label="Properties">
              {data.propertyIds.length === 0
                ? 'No properties assigned'
                : `${String(data.propertyIds.length)} property${
                    data.propertyIds.length === 1 ? '' : 'ies'
                  }`}
            </DetailField>
            <DetailField label="Version">{data.version}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
